import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Icon from "@/components/icons/Icons";
import RetirementStepsTitle from "./RetirementStepsTitle";
import { Plan, PlanningMode, RetirementPlanCardData } from "@/type/financialPageTypes";
import { buildPlanCard } from "@/utils/financialPageFunctions";
import RetirementPlanCard from "../ui/RetirementPlanCard";

const swapVariants = {
  enterRight: { opacity: 0, x: 20 },
  enterLeft: { opacity: 0, x: -20 },
  center: { opacity: 1, x: 0 },
  exitLeft: { opacity: 0, x: -20 },
  exitRight: { opacity: 0, x: 20 },
};

const PLANS: RetirementPlanCardData[] = [
  buildPlanCard("SILVER PLAN", "SILVER PLAN", "Create ₹3 Cr.", "8.2 Cr.", [
    { label: "Pension", value: "₹1.95 Cr" },
    { label: "Health Reserve", value: "₹60 L" },
    { label: "Vacations", value: "₹30 L" },
    { label: "Legacy", value: "₹15 L" },
  ]),
  buildPlanCard("GOLD PLAN", "GOLD PLAN", "Create ₹5 Cr.", "₹13.4 Cr.", [
    { label: "Pension", value: "₹3.4 Cr" },
    { label: "Health Reserve", value: "₹90 L" },
    { label: "Vacations", value: "₹45 L." },
    { label: "Legacy", value: "₹25 L" },
  ]),
  buildPlanCard("PLATINUM PLAN", "PLATINUM PLAN", "Create ₹10 Cr.", "₹27.7 Cr.", [
    { label: "Pension", value: "₹7 Cr" },
    { label: "Health Reserve", value: "₹1.3 Cr" },
    { label: "Vacations", value: "₹90 L." },
    { label: "Legacy", value: "₹80 L." },
  ]),
];

const PLANNING_OPTIONS = [
  {
    id: "salary" as PlanningMode,
    label: "Based on my salary",
    description: "Invest a % of your monthly income and see your projected corpus.",
    icon: "💼",
  },
  {
    id: "expenses" as PlanningMode,
    label: "Based on desired future expenses",
    description: "Tell us your retirement lifestyle cost and we'll reverse-calculate your SIP.",
    icon: "🎯",
  },
  {
    id: "custom" as PlanningMode,
    label: "Want to enter Custom Retirement amount?",
    description: "Tell us your custom Retirement amount and we'll calculate your SIP.",
    icon: "💰",
  },
];

export default function RetirementChoosePlanScreen({
  stepTitle,
  planningStepTitle,
  selectedPlan,
  onSelectPlan,
  planningMode,
  onSelectMode,
  customAmount: _customAmount,
  onCustomAmount: _onCustomAmount,
  validateTrigger,
}: {
  stepTitle?: string;
  planningStepTitle?: string;
  selectedPlan: Plan;
  onSelectPlan: (p: Plan) => void;
  planningMode: PlanningMode;
  onSelectMode: (m: PlanningMode) => void;
  customAmount: string;
  onCustomAmount: (v: string) => void;
  validateTrigger?: number;
}) {
  const [showCustom, setShowCustom] = useState(planningMode !== null);
  const [error, setError] = useState("");

  // Show error when modal's Continue fires and nothing is selected
  const prevTrigger = useRef(0);

  useEffect(() => {
    if (!validateTrigger || validateTrigger === prevTrigger.current) return;
    prevTrigger.current = validateTrigger;
    if (!selectedPlan && !planningMode) {
      setError("Please select a retirement plan or choose a planning mode to continue.");
    }
  }, [validateTrigger, selectedPlan, planningMode]);

  // Clear error as soon as user makes a selection
  useEffect(() => {
    if (selectedPlan || planningMode) setError("");
  }, [selectedPlan, planningMode]);

  const handleShowCustom = () => {
    onSelectPlan(null);
    setShowCustom(true);
  };

  const handleShowPlans = () => {
    onSelectMode(null);
    setShowCustom(false);
  };

  return (
    <div className="flex flex-col h-full">
      <AnimatePresence mode="wait">
        {!showCustom ? (
          <motion.div
            key="plans-view"
            initial={swapVariants.enterLeft}
            animate={swapVariants.center}
            exit={swapVariants.exitLeft}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="flex flex-col"
          >
            <RetirementStepsTitle
              stepTitle={stepTitle || "Invest with our Pre-Built Retirement Packages"}
              stepSubTitle="Select your retirement package and we'll show your monthly income, health cover, and legacy wealth."
            />
            {/* Cards */}
            <div className="flex gap-3 pb-4 overflow-x-auto overflow-y-hidden sm:overflow-visible scrollbar-none">
              {PLANS.map((plan) => (
                <RetirementPlanCard key={plan.id} plan={plan} selectedPlan={selectedPlan} onSelect={onSelectPlan} />
              ))}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="custom-view"
            initial={swapVariants.enterRight}
            animate={swapVariants.center}
            exit={swapVariants.exitRight}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="flex flex-col"
          >
            <RetirementStepsTitle stepTitle={planningStepTitle || "How would you like to plan?"} />

            {PLANNING_OPTIONS.map((opt) => {
              const isSelected = planningMode === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => onSelectMode(opt.id)}
                  className={`relative w-full text-left rounded-2xl border-2 p-3 sm:px-4 sm:py-3 xl:p-4.5 mb-3 last-of-type:mb-0 transition ${
                    isSelected
                      ? "border-primaryBlue bg-[#EFF3FF]"
                      : "border-[#DBDBDB] bg-white hover:border-primaryBlue/30 hover:bg-[#f5f8ff]"
                  }`}
                >
                  <div className="flex items-center gap-2 sm:gap-3.5">
                    <span
                      className={[
                        "flex shrink-0 w-4 h-4 rounded-full text-white items-center justify-center",
                        isSelected ? "bg-[#20D000]" : "border-2 border-[#494953]",
                      ].join(" ")}
                    >
                      {isSelected && <Icon.rightSignIcon className="w-3 h-3" />}
                    </span>

                    <span className="hidden sm:inline-flex items-center justify-center shrink-0 rounded-xl bg-primaryBlue/10 p-3">
                      {opt.icon}
                    </span>
                    <div className="flex-1">
                      <p
                        className={`text-2sm leading-1.2 sm:text-base sm:leading-1.2 font-semibold ${isSelected ? "text-primaryBlue" : "text-primaryBlack"}`}
                      >
                        {opt.label}
                      </p>
                      <p className="text-xs sm:text-2xs font-medium text-[#666666] mt-1">{opt.description}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-auto">
        {error && <p className="mt-4 text-xs text-red-500 text-center">{error}</p>}

        <button
          onClick={showCustom ? handleShowPlans : handleShowCustom}
          className="mt-1.5 w-full flex items-center justify-center gap-2 bg-[#ECF4FF] border border-primaryBlue rounded-2xl p-3 sm:py-4 sm:px-4 text-primaryBlue transition sm:hover:bg-primaryBlue/10"
        >
          {showCustom && <Icon.rightArrow className="rotate-180 w-4 h-4 sm:w-6 sm:h-6" />}
          {/* Desktop Text */}
          <p className="font-semibold leading-1.2 hidden sm:block">
            Want to get {showCustom ? "Pre-built" : "fully Customised"} Retirement Plan?
          </p>

          {/* Mobile Text */}
          <p className="text-sm font-medium leading-1.2 sm:hidden text-left underline underline-offset-2">
            Get {showCustom ? "Pre-Built" : "Customised"} Plan?
          </p>
          <span className="hidden sm:inline-block underline underline-offset-2">Click here</span>
          {!showCustom && <Icon.rightArrow className="w-4 h-4 sm:w-6 sm:h-6" />}
        </button>
      </div>
    </div>
  );
}
